// PublicationsBadge — small top-bar indicator that surfaces only when
// at least one FAA publication is expiring soon or expired. Taps
// navigate to the Data Currency section in Settings.
//
// 'unknown' state (store not yet loaded) now renders a subtle chip so
// the pilot can distinguish 'current and good' from 'we have no idea'
// — especially important offline when the server is unreachable (OAC6).

import { useEffect } from 'react';
import { AlertTriangle, XCircle } from '../shell/Icon';
import { useNavigate } from 'react-router-dom';
import { usePublicationsStore } from '../stores/publicationsStore';

export default function PublicationsBadge() {
  const snapshot = usePublicationsStore((s) => s.snapshot);
  const ensureFresh = usePublicationsStore((s) => s.ensureFresh);
  const nav = useNavigate();

  useEffect(() => {
    void ensureFresh();
  }, [ensureFresh]);

  if (!snapshot) {
    // No data at all (not from memory or IDB). Render a subtle 'unknown'
    // chip so the pilot can see data currency is indeterminate rather
    // than silently nothing (OAC6).
    return (
      <button
        onClick={() => nav('/settings')}
        title="Chart currency unknown — tap to check data status"
        aria-label="Chart currency unknown"
        className="flex items-center gap-1 rounded-md border border-gray-600 px-2 py-1 text-[11px] bg-gray-800/40 text-gray-400 hover:opacity-80"
      >
        <span>Charts ?</span>
      </button>
    );
  }
  if (snapshot.overall !== 'expiring' && snapshot.overall !== 'expired') return null;

  const expired = snapshot.overall === 'expired';
  const Icon = expired ? XCircle : AlertTriangle;
  const colorClass = expired ? 'text-red-400' : 'text-amber-400';
  const bgClass    = expired ? 'bg-red-900/30 border-red-700' : 'bg-amber-900/30 border-amber-700';
  const label      = expired ? 'Charts expired' : 'Charts expiring';

  // Build a short tooltip listing the offending publications.
  const offenders = snapshot.publications
    .filter((p) => p.status === 'expired' || p.status === 'expiring')
    .map((p) => `${p.id.toUpperCase()} ${p.expires ?? ''}`)
    .join(' · ');

  return (
    <button
      onClick={() => nav('/settings')}
      title={offenders || label}
      aria-label={label}
      className={`flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] ${bgClass} ${colorClass} hover:opacity-80`}
    >
      <Icon size={12} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
